import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-types-papi';
import ShopThumb, {
  ShopThumbType,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import StandardThumb from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard';
import {
  getDeviationResourceType,
  getSuggestionReasonsByPriority,
} from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import {
  BiLoggerContextProvider,
  BiEventType,
} from '@wix/da-shared-react/pkg/biLogger';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import getBiDataForDeviationLink from '@wix/da-shared-react/pkg/Duperbrowse/DeviationLink/getBiDataForDeviationLink';
import {
  DeviationTypes,
  DeviationSuggestionReasons,
} from '@wix/da-shared-react/pkg/types/deviation';
import { NullableDropdownItem } from '@wix/da-shared-react/pkg/Dropdown/types';
import { showLessLikeThis } from '../../actions/recommendation';
import s from './ItemCard.scss';

export interface Props {
  deviation: PapiDeviation;
  offset: number;
  width: number;
  height: number;
  style?: React.CSSProperties;
  canShowLess?: boolean;
  responsive?: boolean;
  className?: string;
  onShowLess: typeof showLessLikeThis;
}
const nonDeviationTypes = [
  DeviationTypes.COMMISSION,
  DeviationTypes.POLL,
  DeviationTypes.STATUS,
  DeviationTypes.UNKNOWN,
];

const suggestionReasonToNameMap = {
  [DeviationSuggestionReasons.DailyDeviation]: 'daily_deviation',
  [DeviationSuggestionReasons.NewDeviant]: 'new_deviant',
  [DeviationSuggestionReasons.Popular]: 'popular',
  [DeviationSuggestionReasons.Recommended]: 'recommended',
  [DeviationSuggestionReasons.WatchingGroup]: 'watching_group',
  [DeviationSuggestionReasons.WatchingTopic]: 'watching_topic',
  [DeviationSuggestionReasons.WatchingUser]: 'watching_user',
};

const DeviationCard: React.FC<Props> = ({
  deviation,
  offset,
  width,
  height,
  style,
  responsive,
  canShowLess,
  className,
  onShowLess,
}) => {
  const { t } = useTranslation();

  const reasonShown =
    getSuggestionReasonsByPriority(deviation)
      ?.map(reason => suggestionReasonToNameMap[reason] ?? 'unknown')
      .join(',') || 'unknown';
  const devType = DeviationMediaService.getDeviationType(deviation);
  const hasShowLess = canShowLess && !nonDeviationTypes.includes(devType);
  const actions: NullableDropdownItem[] = useMemo(
    () => [
      {
        label: t('itemCard.showLess'),
        onClick: () =>
          onShowLess({
            deviationId: deviation.deviationId,
            offset,
            typeId: getDeviationResourceType(deviation),
            reason: reasonShown,
            ownerId: deviation.author?.userId,
          }),
        biData: {
          ...getBiDataForDeviationLink(deviation),
          evid: BiEventType.SHOW_LESS_LIKE_THIS,
        },
      },
    ],
    [onShowLess, deviation, offset, reasonShown, t]
  );

  const thumb =
    devType === DeviationTypes.COMMISSION ? (
      <ShopThumb
        deviation={deviation}
        width={width}
        height={height}
        type={ShopThumbType.COMMISSION}
        style={style}
        className={className}
      />
    ) : (
      <div className={classnames(s['thumb'], className)}>
        <StandardThumb
          deviation={deviation}
          width={width}
          height={height}
          style={style}
          responsive={responsive}
          extraActions={hasShowLess ? actions : undefined}
        />
      </div>
    );
  return (
    <BiLoggerContextProvider value={{ reason_shown: reasonShown }}>
      {thumb}
    </BiLoggerContextProvider>
  );
};
DeviationCard.displayName = 'DeviationCard';

export default DeviationCard;
